import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCcw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, Package, AlertTriangle, CheckCircle2,
  TrendingUp, Filter, Calendar, ShieldAlert, ShieldCheck, Clock,
} from "lucide-react";
import apinode from "../../services/apinode";

// ─── helpers ────────────────────────────────────────────────
const dtBR = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

const translateStatus = (s) => {
  if (!s) return "—";
  const map = {
    opened: "Aberto", open: "Aberto", closed: "Fechado", paid: "Pago",
    cancelled: "Cancelado", partially_refunded: "Parcial Reembolsado",
    in_progress: "Em andamento", mediations: "Mediação", returns: "Devolução",
    dispute: "Disputa", none: "—", claim: "Reclamação", return: "Devolução",
    refund: "Reembolso", shipped: "Enviado", delivered: "Entregue",
    accepted: "Aceito", rejected: "Rejeitado", seller: "Vendedor",
    buyer: "Comprador", system: "Sistema",
  };
  return map[s] || s;
};

const STATUS_COLORS = {
  Aberto:                { bg: "#fef3c7", color: "#92400e" },
  Fechado:               { bg: "#dbeafe", color: "#1e40af" },
  Pago:                  { bg: "#d1fae5", color: "#065f46" },
  Cancelado:             { bg: "#fee2e2", color: "#991b1b" },
  "Parcial Reembolsado": { bg: "#ede9fe", color: "#5b21b6" },
  "Em andamento":        { bg: "#ffedd5", color: "#9a3412" },
  Mediação:              { bg: "#e0f2fe", color: "#075985" },
  Devolução:             { bg: "#f0fdf4", color: "#166534" },
  Reclamação:            { bg: "#fef2f2", color: "#991b1b" },
  Reembolso:             { bg: "#fff7ed", color: "#9a3412" },
};

const Badge = ({ value }) => {
  const label = translateStatus(value);
  const style  = STATUS_COLORS[label] || { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{
      background: style.bg, color: style.color,
      padding: "3px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
      fontFamily: "'DM Mono', monospace",
    }}>
      {label}
    </span>
  );
};

const getMonthRange = () => {
  const now   = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt   = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
};

// ─── componente principal ───────────────────────────────────
export default function ClaimsAdmin() {
  const [raw, setRaw]         = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // filtros do front (apelido e status ficam no JS — sem nova requisição)
  const [apelido, setApelido] = useState("");
  const [status, setStatus]   = useState("");

  // datas controlam a requisição ao backend
  const { start: ds, end: de } = getMonthRange();
  const [dateStart, setDateStart] = useState(ds);
  const [dateEnd,   setDateEnd]   = useState(de);

  const [page, setPage]               = useState(1);
  const pageSize                      = 15;
  const [openRow, setOpenRow]         = useState(null);
  const [details, setDetails]         = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);

  // ── identidades ─────────────────────────────────────────
  // conta   → qual banco Firebird conectar (param da rota)
  // idConta → id da loja no WHERE do SQL   (query param id_conta)
  // Podem ser iguais ou diferentes conforme sua configuração.
  const conta   = localStorage.idconta    // ex: "514066249"  — banco a conectar
  const idConta = localStorage.idconta  // ex: "123"        — loja no SQL

  // ── busca principal (dispara ao montar e ao mudar datas) ─
  const fetchData = useCallback(async () => {
    if (!conta || !idConta) {
      setError("Sessão inválida: conta ou id_conta não encontrados.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        id_conta:   idConta,
        date_start: dateStart,
        date_end:   dateEnd,
      });
      const r = await apinode.get(`/meli/claims/list/${conta}?${params}`);
      setRaw(r?.data?.data || []);
      setPage(1);
    } catch (err) {
      console.error("Erro ao buscar claims:", err);
      setError("Erro ao carregar claims. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [conta, idConta, dateStart, dateEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── detalhe por claim ────────────────────────────────────
  const fetchDetail = async (claimId) => {
    try {
      setLoadingDetail(claimId);
      const res = await apinode.get(`/meli/claims/${claimId}/detail/${conta}`);
      setDetails(prev => ({ ...prev, [claimId]: res.data }));
    } catch (err) {
      console.error("Erro ao buscar detalhe:", err);
    } finally {
      setLoadingDetail(null);
    }
  };

  // ── filtros locais (sem nova requisição) ─────────────────
  const filtered = useMemo(() =>
    raw.filter(r =>
      (apelido ? r.apelido === apelido : true) &&
      (status  ? r.status  === status  : true)
    ),
  [raw, apelido, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  // ── stats ────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:     filtered.length,
    abertos:   filtered.filter(r => ["open","opened","in_progress"].includes(r.status)).length,
    fechados:  filtered.filter(r => r.status === "closed").length,
    reputacao: filtered.filter(r => r.affects_reputation === true || r.affects_reputation === 1 || r.affects_reputation === "true").length,
    semana:    filtered.filter(r => new Date(r.date_created) >= new Date(Date.now() - 7 * 86400000)).length,
  }), [filtered]);

  const uniqueApelidos = [...new Set(raw.map(r => r.apelido).filter(Boolean))];
  const uniqueStatus   = [...new Set(raw.map(r => r.status).filter(Boolean))];

  // ── paginação ────────────────────────────────────────────
  const getPageNumbers = () => {
    const delta = 2, range = [];
    for (let i = Math.max(2, safePage - delta); i <= Math.min(totalPages - 1, safePage + delta); i++) range.push(i);
    if (safePage - delta > 2) range.unshift("...");
    if (safePage + delta < totalPages - 1) range.push("...");
    range.unshift(1);
    if (totalPages > 1) range.push(totalPages);
    return range;
  };

  // ── render ───────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#f1f5f9", minHeight: "100vh" }}>

      {/* Navbar */}
      <div style={{
        background: "#0f172a", color: "#fff",
        padding: "0 2rem", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 50,
        boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: 16 }}>
          <Package size={20} color="#38bdf8" />
          Claims Admin
          <span style={{ fontSize: 11, background: "#1e293b", color: "#94a3b8", padding: "2px 8px", borderRadius: 20, marginLeft: 4 }}>
            conta #{idConta}
          </span>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            background: loading ? "#1e293b" : "#38bdf8",
            color: loading ? "#64748b" : "#0f172a",
            border: "none", borderRadius: 8,
            padding: "6px 14px", cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 700, fontSize: 13,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          <RefreshCcw size={14} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          {loading ? "Carregando…" : "Atualizar"}
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16, padding: "20px 24px 8px" }}>
        {[
          { label: "Total no período",  value: stats.total,     icon: <Package size={16} />,       color: "#0ea5e9" },
          { label: "Abertos",           value: stats.abertos,   icon: <AlertTriangle size={16} />, color: "#f59e0b" },
          { label: "Fechados",          value: stats.fechados,  icon: <CheckCircle2 size={16} />,  color: "#10b981" },
          { label: "Afetam reputação",  value: stats.reputacao, icon: <ShieldAlert size={16} />,   color: "#ef4444" },
          { label: "Últimos 7 dias",    value: stats.semana,    icon: <TrendingUp size={16} />,    color: "#8b5cf6" },
        ].map(({ label, value, icon, color }) => (
          <div key={label} style={{
            background: "#fff", borderRadius: 12, padding: "16px 20px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)", borderTop: `3px solid ${color}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
              <span style={{ color }}>{icon}</span> {label}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", letterSpacing: "-1px" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{
        background: "#fff", margin: "8px 24px", borderRadius: 12,
        padding: "12px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
      }}>

        {/* datas — disparam nova requisição ao backend */}
        <Calendar size={16} color="#94a3b8" />
        <input type="date" value={dateStart}
          onChange={e => { setDateStart(e.target.value); setPage(1); }}
          style={inputStyle} />
        <span style={{ color: "#94a3b8", fontSize: 13 }}>até</span>
        <input type="date" value={dateEnd}
          onChange={e => { setDateEnd(e.target.value); setPage(1); }}
          style={inputStyle} />

        <div style={{ width: 1, height: 24, background: "#e2e8f0", margin: "0 4px" }} />

        {/* apelido e status — filtram localmente, sem nova requisição */}
        <Filter size={16} color="#94a3b8" />
        <select value={apelido} onChange={e => { setApelido(e.target.value); setPage(1); }} style={inputStyle}>
          <option value="">Todos os apelidos</option>
          {uniqueApelidos.map(a => <option key={a} value={a}>{a}</option>)}
        </select>

        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} style={inputStyle}>
          <option value="">Todos os status</option>
          {uniqueStatus.map(s => <option key={s} value={s}>{translateStatus(s)}</option>)}
        </select>

        {(apelido || status) && (
          <button onClick={() => { setApelido(""); setStatus(""); setPage(1); }}
            style={{ ...inputStyle, cursor: "pointer", color: "#ef4444", borderColor: "#fecaca", background: "#fff5f5", fontWeight: 600 }}>
            Limpar filtros
          </button>
        )}

        <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 12, fontWeight: 600 }}>
          {filtered.length} registro{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Erro */}
      {error && (
        <div style={{ margin: "0 24px 8px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, padding: "10px 16px", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Tabela */}
      <div style={{
        background: "#fff", margin: "0 24px", borderRadius: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden",
      }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            <RefreshCcw size={24} style={{ animation: "spin 1s linear infinite", marginBottom: 8 }} />
            <div>Carregando claims…</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
            Nenhum claim encontrado para os filtros selecionados.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {["", "ID", "Pedido", "Cliente", "Apelido", "Situação", "Status", "Tipo", "Qtd", "Reputação", "Criado"].map(h => (
                  <th key={h} style={{
                    padding: "10px 14px", textAlign: "left",
                    fontSize: 11, color: "#64748b", fontWeight: 700,
                    letterSpacing: "0.5px", textTransform: "uppercase",
                    borderBottom: "1px solid #e2e8f0",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r, idx) => {
                const opened = openRow === r.id;
                const det    = details[r.id];
                const affRep = r.affects_reputation === true || r.affects_reputation === 1 || r.affects_reputation === "true";

                return (
                  <React.Fragment key={r.id}>
                    <tr
                      style={{ background: idx % 2 === 0 ? "#fff" : "#f8fafc" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                      onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#f8fafc"}
                    >
                      <td style={{ padding: "8px 8px 8px 14px" }}>
                        <button
                          onClick={() => {
                            if (opened) { setOpenRow(null); }
                            else {
                              setOpenRow(r.id);
                              if (!details[r.id]) fetchDetail(r.id);
                            }
                          }}
                          style={{
                            border: "1px solid #e2e8f0",
                            background: opened ? "#dbeafe" : "#fff",
                            borderRadius: 6, padding: "4px 6px", cursor: "pointer",
                            color: opened ? "#2563eb" : "#64748b",
                            display: "flex", alignItems: "center",
                          }}
                        >
                          {opened ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        </button>
                      </td>
                      <td style={tdStyle}><span style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: "#64748b" }}>{r.id}</span></td>
                      <td style={tdStyle}><strong style={{ color: "#0f172a" }}>{r.pedido || "—"}</strong></td>
                      <td style={tdStyle}>{r.cliente || "—"}</td>
                      <td style={tdStyle}>{r.apelido || "—"}</td>
                      <td style={tdStyle}><Badge value={r.situacao} /></td>
                      <td style={tdStyle}><Badge value={r.status} /></td>
                      <td style={tdStyle}><Badge value={r.return_type} /></td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: "#0f172a" }}>{r.claimed_quantity ?? "—"}</td>
                      <td style={tdStyle}>
                        {affRep
                          ? <span style={{ color: "#ef4444", display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600 }}><ShieldAlert size={15} /> Sim</span>
                          : <span style={{ color: "#10b981", display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}><ShieldCheck size={15} /> Não</span>
                        }
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap", fontSize: 12, color: "#64748b" }}>{dtBR(r.date_created)}</td>
                    </tr>

                    {opened && (
                      <tr>
                        <td colSpan={11} style={{ padding: 0 }}>
                          <div style={{
                            background: "#f8fafc",
                            borderTop: "2px solid #e0f2fe", borderBottom: "2px solid #e0f2fe",
                            padding: "16px 24px",
                            display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16,
                          }}>
                            {[
                              ["Stage",      translateStatus(r.stage)],
                              ["Conferente", r.conferente],
                              ["Data Conf.", dtBR(r.data_conferencia)],
                              ["Resolution", translateStatus(r.resolution_reason)],
                              ["Atualizado", dtBR(r.last_updated)],
                              ["Due Date",   r.due_date ? dtBR(r.due_date) : null],
                              ["Incentivo",  r.has_incentive ? "Sim" : "Não"],
                            ].map(([label, val]) => (
                              <div key={label}>
                                <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{label}</div>
                                <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 500 }}>{val || "—"}</div>
                              </div>
                            ))}

                            {loadingDetail === r.id && (
                              <div style={{ gridColumn: "1/-1", color: "#94a3b8", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                                <Clock size={14} /> Carregando detalhes…
                              </div>
                            )}

                            {det && (
                              <>
                                <div style={{ gridColumn: "1/-1", height: 1, background: "#e2e8f0", margin: "4px 0" }} />
                                {[
                                  ["Título",      det.title],
                                  ["Descrição",   det.description],
                                  ["Problema",    det.problem],
                                  ["Responsável", det.action_responsible],
                                ].map(([label, val]) => (
                                  <div key={label}>
                                    <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{label}</div>
                                    <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 500 }}>{val || "—"}</div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Paginação */}
        {!loading && filtered.length > 0 && (
          <div style={{
            display: "flex", justifyContent: "center", alignItems: "center",
            gap: 4, padding: "12px 16px", borderTop: "1px solid #f1f5f9",
          }}>
            <PgBtn onClick={() => setPage(1)} disabled={safePage === 1}><ChevronsLeft size={14} /></PgBtn>
            <PgBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}><ChevronLeft size={14} /></PgBtn>

            {getPageNumbers().map((num, i) =>
              num === "..." ? (
                <span key={i} style={{ padding: "0 4px", color: "#94a3b8" }}>…</span>
              ) : (
                <PgBtn key={i} active={safePage === num} onClick={() => setPage(num)}>{num}</PgBtn>
              )
            )}

            <PgBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}><ChevronRight size={14} /></PgBtn>
            <PgBtn onClick={() => setPage(totalPages)} disabled={safePage === totalPages}><ChevronsRight size={14} /></PgBtn>

            <span style={{ marginLeft: 12, fontSize: 12, color: "#94a3b8" }}>
              Página {safePage} de {totalPages}
            </span>
          </div>
        )}
      </div>

      <div style={{ height: 32 }} />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── sub-componentes ────────────────────────────────────────
const inputStyle = {
  padding: "6px 10px", border: "1px solid #e2e8f0",
  borderRadius: 8, fontSize: 13, color: "#374151",
  background: "#fff", outline: "none",
};

const tdStyle = {
  padding: "10px 14px", fontSize: 13,
  borderBottom: "1px solid #f1f5f9", color: "#374151",
};

function PgBtn({ onClick, disabled, active, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      border: active ? "none" : "1px solid #e2e8f0",
      background: active ? "#0ea5e9" : disabled ? "#f8fafc" : "#fff",
      color: active ? "#fff" : disabled ? "#cbd5e1" : "#374151",
      minWidth: 34, height: 32, borderRadius: 6,
      cursor: disabled ? "not-allowed" : "pointer",
      fontSize: 13, fontWeight: active ? 700 : 500,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {children}
    </button>
  );
}